import { X, Check, Star } from "lucide-react";
import { Button } from "../ui/button";

const memberships = [
  {
    name: "일반",
    nameEn: "Basic",
    price: "무료",
    priceDetail: "",
    description: "New Coupling을 처음 시작하는 분들을 위한 기본 멤버십",
    features: [
      "커뮤니티 읽기",
      "유튜브 콘텐츠 시청",
      "기본 이벤트 참여",
    ],
    notIncluded: ["매칭 서비스", "코칭 프로그램", "프리미엄 이벤트"],
    popular: false,
    buttonText: "가입하기",
  },
  {
    name: "정회원",
    nameEn: "Regular",
    price: "USD $19",
    priceDetail: "/월",
    description: "본격적인 매칭과 커뮤니티 활동을 원하는 분들께",
    features: [
      "커뮤니티 글쓰기/읽기",
      "줌미팅 월 2회 참여",
      "오프모임 10% 할인",
    ],
    notIncluded: ["1:1 코칭", "VIP 이벤트"],
    popular: false,
    buttonText: "시작하기",
  },
  {
    name: "프리미엄",
    nameEn: "Premium",
    price: "USD $49",
    priceDetail: "/월",
    description: "적극적인 매칭과 코칭을 원하는 분들께 추천",
    features: [
      "정회원 혜택 모두 포함",
      "줌미팅 무제한 참여",
      "오프모임 20% 할인",
      "코칭 프로그램 50% 할인",
      "소개팅 월 3회",
      "줌 상담 월 1회",
    ],
    notIncluded: [],
    popular: true,
    buttonText: "가장 인기",
  },
  {
    name: "VIP",
    nameEn: "VIP",
    price: "USD $99",
    priceDetail: "/월",
    description: "최고의 서비스와 1:1 맞춤 케어를 원하는 분들께",
    features: [
      "프리미엄 혜택 모두 포함",
      "코칭 프로그램 무료",
      "소개팅 무제한",
      "줌 상담 월 4회",
      "전담 매니저 배정",
      "VIP 전용 이벤트",
      "프리미엄 프로필 노출",
    ],
    notIncluded: [],
    popular: false,
    buttonText: "VIP 신청",
  },
];

export const MembershipModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center"
      data-testid="membership-modal-overlay"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div 
        className="relative bg-[#FAFAF9] rounded-2xl shadow-2xl w-[95%] max-w-6xl max-h-[90vh] overflow-hidden"
        data-testid="membership-modal-content"
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#FAFAF9] border-b border-stone-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <span className="text-gold-500 font-medium text-xs tracking-wider uppercase block mb-1">
              Membership
            </span>
            <h2 className="font-sans text-2xl md:text-3xl font-[720] text-stone-800">
              멤버십 가입표
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-full transition-colors"
            data-testid="membership-modal-close"
          >
            <X className="w-6 h-6 text-stone-600" />
          </button>
        </div>
        
        {/* Body */}
        <div className="overflow-y-auto max-h-[calc(90vh-100px)] p-6">
          <p className="text-stone-600 text-base mb-6 text-center">
            다양한 멤버십 옵션으로 당신의 니즈에 맞는 최적의 서비스를 제공합니다.
          </p>
          
          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {memberships.map((membership, index) => (
              <div
                key={membership.name}
                className={`relative bg-white rounded-xl p-5 ${
                  membership.popular
                    ? "ring-2 ring-burgundy-500 shadow-lg"
                    : "border border-stone-200"
                } transition-all duration-300`}
                data-testid={`modal-membership-card-${index}`}
              >
                {/* Popular Badge */}
                {membership.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-burgundy-500 text-white text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3 fill-current" />
                    인기
                  </div>
                )}

                {/* Plan Name */}
                <div className="mb-4 pt-2">
                  <span className="text-xs text-gold-500 font-medium uppercase tracking-wider">
                    {membership.nameEn}
                  </span>
                  <h3 className="font-sans text-xl font-[720] text-stone-800 mt-1">
                    {membership.name}
                  </h3>
                </div>

                {/* Price */}
                <div className="mb-4">
                  <span className="font-sans text-3xl font-[720] text-burgundy-500">
                    {membership.price}
                  </span>
                  <span className="text-stone-500 text-sm">
                    {membership.priceDetail}
                  </span>
                </div>

                {/* Description */}
                <p className="text-stone-600 text-xs mb-4 leading-relaxed">
                  {membership.description}
                </p>

                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {membership.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start gap-2 text-xs text-stone-700"
                    >
                      <Check className="w-4 h-4 text-burgundy-500 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                  {membership.notIncluded.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start gap-2 text-xs text-stone-400 line-through"
                    >
                      <Check className="w-4 h-4 text-stone-300 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <Button
                  className={`w-full text-sm ${
                    membership.popular ? "btn-primary" : "btn-secondary"
                  }`}
                >
                  {membership.buttonText}
                </Button>
              </div>
            ))}
          </div>

          {/* Refund Policy */}
          <div className="mt-6 text-center">
            <p className="text-stone-500 text-sm">
              * 환불 정책: 잔여 개월수의 30%를 환불해드립니다.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
